import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute.jsx';
import { SkeletonPage } from './components/Skeleton.jsx';
import CookieConsent from './components/CookieConsent.jsx';
import SyncToast from './components/SyncToast.jsx';

// Route components are lazy-loaded so each screen ships as its OWN chunk. The
// dashboard (and every other route) no longer downloads and parses the whole
// app on first paint — Vite emits one chunk per import() below, fetched only
// when that route is visited. This is the core of the bundle-split perf fix.
const Login = lazy(() => import('./pages/Login.jsx'));
const Signup = lazy(() => import('./pages/Signup.jsx'));
const OAuthConsent = lazy(() => import('./pages/OAuthConsent.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Terms = lazy(() => import('./pages/Terms.jsx'));
const Privacy = lazy(() => import('./pages/Privacy.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));
const SettingsHub = lazy(() => import('./pages/SettingsHub.jsx'));
const SettingsAppearance = lazy(() => import('./pages/SettingsAppearance.jsx'));
const SettingsNotifications = lazy(() => import('./pages/SettingsNotifications.jsx'));
const AddWin = lazy(() => import('./pages/AddWin.jsx'));
const Search = lazy(() => import('./pages/Search.jsx'));
const Finance = lazy(() => import('./pages/Finance.jsx'));
const WinDetail = lazy(() => import('./pages/WinDetail.jsx'));
const WinHistory = lazy(() => import('./pages/WinHistory.jsx'));
const EditWin = lazy(() => import('./pages/EditWin.jsx'));
// Export flow: entry (format picker) → builder → rendered document. Three routes
// under /export/*, mirroring the /win/:id/* drill-down naming.
const Export = lazy(() => import('./pages/Export.jsx'));
const ExportBuilder = lazy(() => import('./pages/ExportBuilder.jsx'));
const ExportDocument = lazy(() => import('./pages/ExportDocument.jsx'));
const Onboarding = lazy(() => import('./pages/Onboarding.jsx'));
const Categories = lazy(() => import('./pages/Categories.jsx'));
const Streak = lazy(() => import('./pages/Streak.jsx'));
const Trash = lazy(() => import('./pages/Trash.jsx'));
const Profile = lazy(() => import('./pages/Profile.jsx'));

// ── Offline app-shell warming ──────────────────────────────────────────────
// Each screen above ships as its own chunk, fetched (and SW-cached) only when
// first visited. So a screen you never opened online is missing offline — e.g.
// an installed PWA opens the Dashboard offline (already visited) but Add Win
// won't load. To make EVERY screen work offline, warm the route chunks in the
// background after boot: importing each one fetches its chunk, which the service
// worker caches (cache-first, same-origin). Sequential + idle so it never
// competes with the screen the user is actually on. No-op offline (nothing to
// fetch) and harmless online (each chunk is fetched at most once).
const WARM_ROUTES = [
  () => import('./pages/AddWin.jsx'),
  () => import('./pages/Search.jsx'),
  () => import('./pages/Finance.jsx'),
  () => import('./pages/WinDetail.jsx'),
  () => import('./pages/WinHistory.jsx'),
  () => import('./pages/EditWin.jsx'),
  () => import('./pages/Categories.jsx'),
  () => import('./pages/Streak.jsx'),
  () => import('./pages/Trash.jsx'),
  () => import('./pages/Profile.jsx'),
  () => import('./pages/SettingsHub.jsx'),
  () => import('./pages/SettingsAppearance.jsx'),
  () => import('./pages/SettingsNotifications.jsx'),
  () => import('./pages/Export.jsx'),
  () => import('./pages/ExportBuilder.jsx'),
  () => import('./pages/ExportDocument.jsx'),
];

function warmRouteChunks() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  let i = 0;
  const schedule = (fn) => {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 300);
  };
  const next = () => {
    if (i >= WARM_ROUTES.length) return;
    const load = WARM_ROUTES[i++];
    load().catch(() => {}).finally(() => schedule(next));
  };
  next();
}

// Routes:
//  /                       — redirect to /dashboard (app entry)
//  /login                  — public
//  /terms                  — public (legal page; reachable without an account)
//  /privacy                — public (legal page; reachable without an account)
//  /404                    — public (the Not Found screen, also reachable directly)
//  /dashboard              — protected
//  /settings/appearance    — protected (app screen: theme picker)
//  *                       — any unknown URL renders the 404 screen.
export default function App() {
  // Warm the lazy route chunks once, shortly after boot, so every screen is
  // available offline (not just the ones already visited). Idle-scheduled so it
  // never delays first paint or the current screen's data.
  useEffect(() => {
    let cancelled = false;
    const kick = () => { if (!cancelled) warmRouteChunks(); };
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(kick, { timeout: 3000 });
      return () => { cancelled = true; if (typeof cancelIdleCallback === 'function') cancelIdleCallback(id); };
    }
    const t = setTimeout(kick, 1500);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  return (
    // One Suspense boundary for all lazy routes. The fallback shows only while a
    // route chunk is in flight (usually a blink); it reuses the same layout-shaped
    // skeleton ProtectedRoute shows while auth initialises, so a slow chunk fetch
    // (e.g. /export/document) renders a page-shaped shimmer, not blank "Loading…".
    <>
      {/* Cookie-consent gate — renders on every route (outside Suspense so a
          suspending route chunk can't hide it) and gates PostHog init. */}
      <CookieConsent />
      {/* App-wide toast for a queued offline write the server later rejected. */}
      <SyncToast />
      <Suspense fallback={<SkeletonPage />}>
        <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<Login />} />
        {/* Public: signup manages its own auth inline (like /login), so it must
            NOT sit behind ProtectedRoute — a logged-out visitor must reach it. */}
        <Route path="/signup" element={<Signup />} />
        {/* Public: the OAuth consent screen manages its own sign-in inline, so
            it must NOT be wrapped in ProtectedRoute (that would bounce an
            unauthenticated visitor to /login and lose the request_id). This is
            where the MCP worker's /oauth/authorize redirects the browser. */}
        <Route path="/oauth-consent" element={<OAuthConsent />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/404" element={<NotFound />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/appearance"
          element={
            <ProtectedRoute>
              <SettingsAppearance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/notifications"
          element={
            <ProtectedRoute>
              <SettingsNotifications />
            </ProtectedRoute>
          }
        />
        {/* Onboarding: a signed-in but not-yet-onboarded user lands here after
            signup (and the Dashboard gate bounces one here if reached directly).
            Protected — onboarding is persisted against the signed-in user. */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add"
          element={
            <ProtectedRoute>
              <AddWin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/search"
          element={
            <ProtectedRoute>
              <Search />
            </ProtectedRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <ProtectedRoute>
              <Finance />
            </ProtectedRoute>
          }
        />
        <Route
          path="/win/:id"
          element={
            <ProtectedRoute>
              <WinDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/win/:id/history"
          element={
            <ProtectedRoute>
              <WinHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/win/:id/edit"
          element={
            <ProtectedRoute>
              <EditWin />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export"
          element={
            <ProtectedRoute>
              <Export />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export/build"
          element={
            <ProtectedRoute>
              <ExportBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export/document"
          element={
            <ProtectedRoute>
              <ExportDocument />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/streak"
          element={
            <ProtectedRoute>
              <Streak />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trash"
          element={
            <ProtectedRoute>
              <Trash />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
